import { useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  AccountType,
  CreateAccountInput,
  LiquidityCategory,
  Sector,
} from "../../lib/accounts";

const ACCOUNT_TYPES: AccountType[] = [
  "GIROKONTO",
  "SPARKONTO",
  "TAGESGELD",
  "FESTGELD",
  "DEPOT",
  "KRYPTO",
  "P2P",
  "KREDIT",
  "KREDITKARTE",
  "BAUSPAR",
  "VERSICHERUNG",
  "SONSTIGE",
];
const LIQUIDITY_CATEGORIES: LiquidityCategory[] = ["LIQUID", "ANLAGE", "VERBINDLICHKEIT"];
const SECTORS: Sector[] = ["HAUSHALT", "IMMOBILIE", "UNZUGEORDNET"];

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function parseEuroInput(raw: string): number {
  const cleaned = raw.replace(/[€\s]/g, "").replace(/\./g, "").replace(/,/, ".");
  const parsed = Number(cleaned);
  if (!Number.isFinite(parsed)) throw new Error("invalid_amount");
  return Math.round(parsed * 100);
}

interface Props {
  mode: "create" | "edit";
  initial?: Partial<CreateAccountInput>;
  submitting?: boolean;
  errorKey?: string | null;
  onSubmit: (data: CreateAccountInput) => void;
  onCancel?: () => void;
}

export function AccountForm({ mode, initial, submitting, errorKey, onSubmit, onCancel }: Props) {
  const { t } = useTranslation();
  const [name, setName] = useState(initial?.name ?? "");
  const [type, setType] = useState<AccountType>(initial?.type ?? "GIROKONTO");
  const [institute, setInstitute] = useState(initial?.institute ?? "");
  const [liquidityCategory, setLiquidityCategory] = useState<LiquidityCategory>(
    initial?.liquidityCategory ?? "LIQUID",
  );
  const [sector, setSector] = useState<Sector>(initial?.sector ?? "UNZUGEORDNET");
  const [notes, setNotes] = useState(initial?.notes ?? "");
  const [usesImportedTransactions, setUsesImportedTransactions] = useState<boolean>(
    initial?.usesImportedTransactions ?? false,
  );
  const [initialAmount, setInitialAmount] = useState("0,00");
  const [initialDate, setInitialDate] = useState(todayIso());
  const [localError, setLocalError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    let cents = 0;
    try {
      cents = parseEuroInput(initialAmount);
    } catch {
      setLocalError(t("accounts.errors.invalid_amount"));
      return;
    }
    onSubmit({
      name,
      type,
      institute: institute.trim() === "" ? undefined : institute,
      liquidityCategory,
      sector,
      currency: "EUR",
      notes: notes.trim() === "" ? undefined : notes,
      usesImportedTransactions,
      initialBalanceCents: cents,
      initialBalanceDate: initialDate,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <fieldset className="space-y-4">
        <legend className="font-medium">{t("accounts.form.section.master")}</legend>

        <div>
          <label className="block text-sm font-medium mb-1" htmlFor="name">
            {t("accounts.form.label.name")}
          </label>
          <input
            id="name"
            type="text"
            required
            maxLength={80}
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full border border-slate-300 rounded px-3 py-2"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="type">
              {t("accounts.form.label.type")}
            </label>
            <select
              id="type"
              value={type}
              onChange={(e) => setType(e.target.value as AccountType)}
              className="w-full border border-slate-300 rounded px-3 py-2"
            >
              {ACCOUNT_TYPES.map((tp) => (
                <option key={tp} value={tp}>
                  {t(`accounts.types.${tp}`)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="institute">
              {t("accounts.form.label.institute")}
            </label>
            <input
              id="institute"
              type="text"
              maxLength={80}
              value={institute}
              onChange={(e) => setInstitute(e.target.value)}
              className="w-full border border-slate-300 rounded px-3 py-2"
            />
          </div>
        </div>

        <fieldset>
          <legend className="block text-sm font-medium mb-1">
            {t("accounts.form.label.liquidityCategory")}
          </legend>
          <div className="flex gap-4">
            {LIQUIDITY_CATEGORIES.map((c) => (
              <label key={c} className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="liquidityCategory"
                  value={c}
                  checked={liquidityCategory === c}
                  onChange={() => setLiquidityCategory(c)}
                />
                {t(`accounts.liquidity.${c}`)}
              </label>
            ))}
          </div>
        </fieldset>

        <div>
          <label className="block text-sm font-medium mb-1" htmlFor="sector">
            {t("accounts.form.label.sector")}
          </label>
          <select
            id="sector"
            value={sector}
            onChange={(e) => setSector(e.target.value as Sector)}
            className="w-full border border-slate-300 rounded px-3 py-2"
          >
            {SECTORS.map((s) => (
              <option key={s} value={s}>
                {t(`accounts.sector.${s}`)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1" htmlFor="notes">
            {t("accounts.form.label.notes")}
          </label>
          <textarea
            id="notes"
            maxLength={500}
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full border border-slate-300 rounded px-3 py-2"
          />
        </div>

        <div>
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={usesImportedTransactions}
              onChange={(e) => setUsesImportedTransactions(e.target.checked)}
            />
            <span>{t("accounts.form.label.usesImportedTransactions")}</span>
          </label>
          <p className="text-xs text-slate-500 mt-1">
            {t("accounts.form.help.usesImportedTransactions")}
          </p>
        </div>
      </fieldset>

      {mode === "create" && (
        <fieldset className="space-y-4">
          <legend className="font-medium">{t("accounts.form.section.initial")}</legend>
          <p className="text-xs text-slate-500">{t("accounts.form.help.initial")}</p>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1" htmlFor="initialAmount">
                {t("accounts.form.label.initialAmount")}
              </label>
              <input
                id="initialAmount"
                type="text"
                required
                value={initialAmount}
                onChange={(e) => setInitialAmount(e.target.value)}
                placeholder="1.234,56"
                className="w-full border border-slate-300 rounded px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" htmlFor="initialDate">
                {t("accounts.form.label.initialDate")}
              </label>
              <input
                id="initialDate"
                type="date"
                required
                max={todayIso()}
                value={initialDate}
                onChange={(e) => setInitialDate(e.target.value)}
                className="w-full border border-slate-300 rounded px-3 py-2"
              />
            </div>
          </div>
        </fieldset>
      )}

      {(localError || errorKey) && (
        <p className="text-sm text-red-600">{t(localError ?? errorKey ?? "errors.unknown")}</p>
      )}

      <div className="flex justify-end gap-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-slate-300 rounded text-sm"
          >
            {t("common.cancel")}
          </button>
        )}
        <button
          type="submit"
          disabled={submitting}
          className="px-4 py-2 bg-brand-600 hover:bg-brand-700 disabled:bg-slate-400 text-white rounded text-sm"
        >
          {submitting ? t("common.submitting") : t("common.save")}
        </button>
      </div>
    </form>
  );
}
